import { useState } from "react";
import { ShoppingBag, Trash2, Plus, Minus, Send, X, User, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { buildCartMessage, openWhatsApp } from "@/lib/whatsapp";

const TYPE_LABELS = {
  auto: "Nettoyage auto",
  textile: "Textile maison",
  mecanique: "Mécanique",
  surface: "Surfaces extérieures",
};

export default function CartFloat() {
  const { items, count, total, isOpen, openCart, closeCart, removeItem, updateQuantity, clearCart } = useCart();
  const [contact, setContact] = useState({ name: "", phone: "", address: "" });

  const change = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const handleSend = () => {
    if (items.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }
    if (!contact.name || !contact.phone) {
      toast.error("Merci d'indiquer au moins votre nom et téléphone");
      return;
    }
    const message = buildCartMessage(items, total, contact);
    openWhatsApp(message);
    toast.success("Redirection vers WhatsApp… Votre demande est prête à être envoyée.");
  };

  return (
    <>
      {/* Floating cart button — bottom right */}
      <button
        onClick={openCart}
        data-testid="cart-float"
        aria-label="Ouvrir le panier"
        className="fixed bottom-6 right-6 z-40 group"
      >
        <div className="absolute inset-0 rounded-full bg-[#E50914] blur-xl opacity-50 group-hover:opacity-80 transition-opacity" />
        <div className="relative flex items-center gap-3 rounded-full bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] pl-4 pr-5 py-3.5 shadow-2xl border-2 border-white/20 transition-all group-hover:scale-105">
          <div className="relative">
            <ShoppingBag className="w-6 h-6 text-white" strokeWidth={2.2} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2.5 -right-2.5 min-w-[20px] h-5 px-1 rounded-full bg-white text-[#E50914] text-[11px] font-black flex items-center justify-center border border-[#E50914]"
              >
                {count}
              </span>
            )}
          </div>
          <span className="text-white font-black text-sm tabular-nums tracking-tight">
            {total} €
          </span>
        </div>
      </button>

      {/* Side panel */}
      <Sheet open={isOpen} onOpenChange={(o) => (o ? openCart() : closeCart())}>
        <SheetContent
          side="right"
          data-testid="cart-panel"
          className="w-full sm:max-w-md bg-[#0A0A0A] border-l border-white/10 text-white p-0 flex flex-col [&>button]:hidden"
        >
          {/* Header */}
          <SheetHeader className="p-6 border-b border-white/10 text-left space-y-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2.5 text-white text-lg font-black tracking-tight">
                <ShoppingBag className="w-5 h-5 text-[#E50914]" />
                Mon panier
                {count > 0 && (
                  <span className="text-xs font-bold text-white/50">({count})</span>
                )}
              </SheetTitle>
              <button
                onClick={closeCart}
                aria-label="Fermer"
                className="w-8 h-8 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </SheetHeader>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty">
                <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                  <ShoppingBag className="w-6 h-6 text-white/30" />
                </div>
                <p className="text-white/70 font-semibold mb-1">Votre panier est vide</p>
                <p className="text-white/40 text-sm max-w-[240px]">
                  Ajoutez des prestations depuis les pages Nettoyage, Textile ou Mécanique.
                </p>
              </div>
            ) : (
              items.map((it) => (
                <CartLine
                  key={it.id}
                  item={it}
                  onRemove={() => removeItem(it.id)}
                  onDec={() => updateQuantity(it.id, it.quantity - 1)}
                  onInc={() => updateQuantity(it.id, it.quantity + 1)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-white/10 p-6 space-y-4">
              {/* Contact mini-form */}
              <div className="space-y-3">
                <ContactInput icon={User} placeholder="Nom complet *" value={contact.name} onChange={change("name")} testid="cart-name" />
                <ContactInput icon={Phone} type="tel" placeholder="Téléphone *" value={contact.phone} onChange={change("phone")} testid="cart-phone" />
                <ContactInput icon={MapPin} placeholder="Adresse d'intervention" value={contact.address} onChange={change("address")} testid="cart-address" />
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-white/60 text-sm uppercase tracking-wider">Total estimé</span>
                <span className="font-black text-2xl text-[#E50914]" data-testid="cart-total">{total} €</span>
              </div>

              <button
                onClick={handleSend}
                data-testid="cart-send"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold uppercase text-sm tracking-wider px-6 py-4 rounded border border-[#25D366] hover:shadow-[0_0_28px_rgba(37,211,102,0.4)] transition-all"
              >
                <Send className="w-4 h-4" /> Envoyer ma demande
              </button>

              <button
                onClick={clearCart}
                data-testid="cart-clear"
                className="w-full text-xs uppercase tracking-wider text-white/40 hover:text-[#E50914] font-semibold transition-colors"
              >
                Vider le panier
              </button>

              <p className="text-[11px] text-white/40 text-center leading-relaxed">
                Votre demande s'ouvre dans WhatsApp, prête à être envoyée. Aucun paiement en ligne — confirmation par message.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function CartLine({ item, onRemove, onDec, onInc }) {
  const lineTotal = item.price * item.quantity;
  return (
    <div
      data-testid={`cart-line-${item.id}`}
      className="flex items-start gap-3 p-3.5 rounded-xl bg-[#050505] border border-white/10"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#E50914] font-bold mb-1">
          {TYPE_LABELS[item.type] || item.type}
        </div>
        <div className="font-bold text-white text-sm leading-tight">{item.label}</div>
        {item.meta && <div className="text-xs text-white/50 mt-1">{item.meta}</div>}
        <div className="text-sm font-black text-white/80 mt-1.5">
          {item.price > 0 ? `${lineTotal} €` : "Sur devis"}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <button
          onClick={onRemove}
          aria-label="Supprimer"
          data-testid={`cart-remove-${item.id}`}
          className="w-7 h-7 rounded flex items-center justify-center text-white/40 hover:text-[#E50914] hover:bg-[#E50914]/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={onDec}
            aria-label="Diminuer"
            className="w-7 h-7 rounded flex items-center justify-center bg-white/5 border border-white/15 hover:bg-white/10 transition-all"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="w-7 text-center font-black text-sm text-white">{item.quantity}</span>
          <button
            onClick={onInc}
            aria-label="Augmenter"
            className="w-7 h-7 rounded flex items-center justify-center bg-[#E50914]/20 border border-[#E50914]/40 hover:bg-[#E50914]/40 transition-all"
          >
            <Plus className="w-3.5 h-3.5 text-[#E50914]" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactInput({ icon: Icon, type = "text", placeholder, value, onChange, testid }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#050505] border border-white/15 rounded pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#E50914] transition-colors placeholder:text-white/30"
      />
    </div>
  );
}
